/**
 * Copy and value maps for the Encompass operations view.
 *
 * The words matter more here than in most screens. Encompass Partner Connect
 * syncs every loan the lender's instance delivers, so this is the only place
 * that decides who is shown a stranger's pipeline — and the option that sounds
 * safest ("follow their Addy role") is the one that decides nothing at all.
 * Every label below is written to make that distinction impossible to misread.
 */

/** The four powers, as the control's own values. */
export const OUTCOME = {
  FOLLOW: "follow",
  ALL: "all",
  TEAM: "team",
  OWN: "own",
};

/** What the backend persists for a person. */
export const SELECTION = {
  ALL_LOANS: "all_loans",
  TEAM_LOANS: "team_loans",
  OWN_LOANS: "own_loans",
};

/** What the backend persists for the organization. */
export const ORG_DEFAULT = {
  FOLLOW_ADDY_ROLE: "follow_addy_role",
  TEAM_LOANS: "team_loans",
  OWN_LOANS: "own_loans",
};

/**
 * The value to send for a chosen outcome. `null` means "no decision", which
 * hands the person back to the organization default.
 *
 * @param {string} outcome - One of {@link OUTCOME}.
 * @returns {string|null}
 */
export function selectionFor(outcome) {
  if (outcome === OUTCOME.ALL) {
    return SELECTION.ALL_LOANS;
  }
  if (outcome === OUTCOME.TEAM) {
    return SELECTION.TEAM_LOANS;
  }
  if (outcome === OUTCOME.OWN) {
    return SELECTION.OWN_LOANS;
  }

  return null;
}

/** What each of the four powers is called in the control. */
export const OUTCOME_LABEL = {
  [OUTCOME.FOLLOW]: "Follow the organization",
  [OUTCOME.ALL]: "Every imported loan",
  [OUTCOME.TEAM]: "Every loan our team is on",
  [OUTCOME.OWN]: "Only loans they are on",
};

/** How each access level reads on a row. */
export const ACCESS_LABEL = {
  all: "Every imported loan",
  team: "Every loan our team is on",
  own: "Only loans they are on",
};

/** How the organization default reads in a sentence. */
export const ORG_DEFAULT_COPY = {
  [ORG_DEFAULT.FOLLOW_ADDY_ROLE]: {
    label: "Follow each person's Addy role",
    detail:
      "Encompass decides nothing. Whoever their Addy role lets see every loan sees every loan Encompass delivered — including other branches, if this lender's instance covers more than this organization.",
  },
  [ORG_DEFAULT.TEAM_LOANS]: {
    label: "Every loan our team is on",
    detail:
      "Everyone without a decision of their own sees the loans naming somebody with an Addy seat here. Loans belonging entirely to another branch — a team of people nobody here has heard of — are hidden. Usually the right answer for a lender.",
  },
  [ORG_DEFAULT.OWN_LOANS]: {
    label: "Only loans they are on",
    detail:
      "Everyone without a decision of their own is limited to loans naming them personally. The strictest option, and more than most lenders want.",
  },
};

/** Why one person ended up with the visibility they have. */
export const RESOLUTION_COPY = {
  person_selection: "Set here, for this person",
  organization_default: "This organization's default",
  not_encompass: "Encompass decides nothing; their Addy role does",
};

/**
 * How wide an access level is, so a change can be told from a widening.
 *
 * Only the ordering matters. Confirmations are asked on the way up and skipped
 * on the way down, so the numbers exist to compare and nothing else.
 */
const ACCESS_RANK = { own: 1, team: 2, all: 3 };

/**
 * How much a person can open today.
 *
 * When Encompass says nothing, their Addy role decides alone and this screen
 * cannot see the loans — but it does know the role's scope, which is enough to
 * tell "already sees everything" from "sees only their own".
 *
 * @param {Object} subject - A row from the People payload.
 * @returns {number}
 */
export function currentAccessRank(subject) {
  if (subject?.access) {
    return ACCESS_RANK[subject.access] ?? ACCESS_RANK.own;
  }

  return subject?.addyViewScope === "all" ? ACCESS_RANK.all : ACCESS_RANK.own;
}

/**
 * How much a person would open after a change.
 *
 * "Follow the organization" is not a level of its own — it resolves to whatever
 * the organization's default resolves to, which is why choosing it can widen
 * somebody just as much as granting a level directly.
 *
 * @param {string|null} selection - The value about to be sent.
 * @param {Object} subject - The row being changed.
 * @param {string} orgDefault - The organization's default.
 * @returns {number}
 */
export function nextAccessRank(selection, subject, orgDefault) {
  if (selection === SELECTION.ALL_LOANS) {
    return ACCESS_RANK.all;
  }
  if (selection === SELECTION.TEAM_LOANS) {
    return ACCESS_RANK.team;
  }
  if (selection === SELECTION.OWN_LOANS) {
    return ACCESS_RANK.own;
  }

  if (orgDefault === ORG_DEFAULT.TEAM_LOANS) {
    return ACCESS_RANK.team;
  }
  if (orgDefault === ORG_DEFAULT.OWN_LOANS) {
    return ACCESS_RANK.own;
  }

  return subject?.addyViewScope === "all" ? ACCESS_RANK.all : ACCESS_RANK.own;
}

/**
 * Whether a change would show somebody loans they cannot open today.
 *
 * @param {string|null} selection
 * @param {Object} subject
 * @param {string} orgDefault
 * @returns {boolean}
 */
export function widensAccess(selection, subject, orgDefault) {
  return (
    nextAccessRank(selection, subject, orgDefault) > currentAccessRank(subject)
  );
}

/**
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatCount(value) {
  if (typeof value !== "number") {
    return "—";
  }

  return value.toLocaleString();
}
